import React, { useEffect, useMemo, useState } from "react";
import {
    Avatar,
    Box,
    ButtonBase,
    Card,
    CardActionArea,
    Chip,
    Divider,
    Grid,
    TextField,
    Typography,
} from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import config from "../../../../config";

const escapeRegExp = (string) => {
    return String(string).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

const highlightMatch = (text, query) => {
    const value = String(text ?? "");
    const q = String(query ?? "").trim();
    if (!q) return value;

    try {
        const regex = new RegExp(`(${escapeRegExp(q)})`, "ig");
        const parts = value.split(regex);
        const qLower = q.toLowerCase();
        return parts.map((part, idx) => {
            if (part.toLowerCase() !== qLower) return <span key={idx}>{part}</span>;
            return (
                <span
                    key={idx}
                    style={{
                        background: "rgba(255, 235, 59, 0.7)",
                        borderRadius: 4,
                        padding: "0 2px",
                    }}
                >
                    {part}
                </span>
            );
        });
    } catch {
        return value;
    }
};

const safeImg = (pathOrUrl) => {
    if (!pathOrUrl) return null;
    if (String(pathOrUrl).startsWith("http")) return pathOrUrl;
    return `${config.baseUrl}${pathOrUrl}`;
};

const findItemMeta = (lists, itemCode) => {
    if (!itemCode) return null;
    for (const list of lists) {
        if (!Array.isArray(list)) continue;
        const found = list.find((x) => x?.item_code === itemCode);
        if (found) return found;
    }
    return null;
};

const Thumb = ({ src, label }) => {
    const fallback = String(label || "?")
        .trim()
        .slice(0, 2)
        .toUpperCase();

    return (
        <Avatar
            src={src || undefined}
            variant="rounded"
            sx={{
                width: 34,
                height: 34,
                bgcolor: "#f1f3f8",
                color: "#271D1C",
                fontWeight: 700,
                fontSize: 12,
            }}
        >
            {fallback}
        </Avatar>
    );
};

export default function BundleConfigurator({
    item,
    menuBundle,
    addonItems,
    menuAddons,
    selectedOptions,
    setSelectedOptions,
    selectedAddons,
    setSelectedAddons,
    notes,
    setNotes,
    bundleStep,
    setBundleStep,
    classes,
    qty,
}) {
    const categories = menuBundle?.categories || [];
    const [search, setSearch] = useState("");

    const allowedAddonCodes = Array.isArray(item?.allowed_addons)
        ? item.allowed_addons.filter(Boolean)
        : [];
    const hasExtras = allowedAddonCodes.length > 0;

    const steps = useMemo(() => {
        const s = categories.map((c) => ({
            type: "category",
            key: `cat:${c?.name}`,
            label: c?.name || "Options",
            cat: c,
        }));
        if (hasExtras) {
            s.push({ type: "extras", key: "extras", label: "Extra add-ons" });
        }
        s.push({ type: "notes", key: "notes", label: "Notes" });
        return s;
    }, [categories, hasExtras]);

    const notesIndex = steps.findIndex((s) => s.type === "notes");
    const active = steps[bundleStep] || steps[0];

    const isFirstStep = bundleStep <= 0;
    const isLastStep = bundleStep >= steps.length - 1;

    useEffect(() => {
        // Reset search whenever the user changes the active category/step
        setSearch("");
    }, [bundleStep, active?.key]);

    const toggleAddon = (code) => {
        if (!code) return;
        setSelectedAddons((prev) =>
            prev?.includes(code)
                ? prev.filter((x) => x !== code)
                : [...(prev || []), code]
        );
    };

    const renderOptionCard = (cat, opt) => {
        const catName = cat?.name;
        const allowMulti = !!cat?.allow_multiple_select;
        const current = selectedOptions?.[catName];
        const isSelected = allowMulti
            ? Array.isArray(current) &&
              current.some((o) => o?.item_code === opt?.item_code)
            : current?.item_code === opt?.item_code;

        const meta = findItemMeta([addonItems, menuAddons], opt?.item_code);
        const img = safeImg(meta?.image) || meta?.cover || null;
        const label = opt?.label || opt?.item_code || "";
        const price = Number(opt?.added_price) || 0;

        return (
            <Grid item xs={6} sm={4} md={3} key={`${catName}_${opt?.item_code}`}>
                <Card
                    variant="outlined"
                    sx={{
                        borderColor: isSelected ? "#1976d2" : "#e5e9f2",
                        bgcolor: isSelected
                            ? "rgba(25, 118, 210, 0.06)"
                            : "#fff",
                    }}
                >
                    <CardActionArea
                        onClick={() =>
                            setSelectedOptions((prev) => {
                                const next = { ...(prev || {}) };
                                if (allowMulti) {
                                    const list = Array.isArray(next[catName])
                                        ? next[catName]
                                        : [];
                                    const exists = list.some(
                                        (o) =>
                                            o?.item_code === opt?.item_code
                                    );
                                    next[catName] = exists
                                        ? list.filter(
                                              (o) =>
                                                  o?.item_code !==
                                                  opt?.item_code
                                          )
                                        : [...list, opt];
                                } else {
                                    next[catName] = opt;
                                }
                                return next;
                            })
                        }
                        sx={{
                            p: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <Thumb src={img} label={label} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 700,
                                    color: "#271D1C",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {highlightMatch(label, search)}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ color: "#6E7187", fontWeight: 600 }}
                            >
                                +{price}
                            </Typography>
                        </Box>
                        {isSelected ? (
                            <DoneIcon sx={{ color: "#1976d2", fontSize: 18 }} />
                        ) : null}
                    </CardActionArea>
                </Card>
            </Grid>
        );
    };

    const filteredCategoryOptions = useMemo(() => {
        if (active?.type !== "category") return [];
        const options = Array.isArray(active?.cat?.options) ? active.cat.options : [];
        const q = search.trim().toLowerCase();
        if (!q) return options;
        return options.filter((opt) => {
            const label = String(opt?.label || "").toLowerCase();
            const code = String(opt?.item_code || "").toLowerCase();
            return label.includes(q) || code.includes(q);
        });
    }, [active?.type, active?.cat, search]);

    const renderAddonCard = (code) => {
        const isSelected = selectedAddons?.includes(code);
        const meta = findItemMeta([addonItems, menuAddons], code) || {};
        const img = safeImg(meta?.image) || meta?.cover || null;
        const price = Number(meta?.standard_rate) || 0;

        return (
            <Grid item xs={6} sm={4} md={3} key={code}>
                <Card
                    variant="outlined"
                    sx={{
                        borderColor: isSelected ? "#2e7d32" : "#e5e9f2",
                        bgcolor: isSelected
                            ? "rgba(46, 125, 50, 0.06)"
                            : "#fff",
                    }}
                >
                    <CardActionArea
                        onClick={() => toggleAddon(code)}
                        sx={{
                            p: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <Thumb src={img} label={code} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 700,
                                    color: "#271D1C",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {code}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ color: "#6E7187", fontWeight: 600 }}
                            >
                                +{price} {qty > 1 ? `× ${qty}` : ""}
                            </Typography>
                        </Box>
                        {isSelected ? (
                            <DoneIcon sx={{ color: "#2e7d32", fontSize: 18 }} />
                        ) : null}
                    </CardActionArea>
                </Card>
            </Grid>
        );
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {/* Jump-to navigation */}
            <Box
                sx={{
                    px: 2,
                    pt: 2,
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                }}
            >
                {steps.map((s, idx) => (
                    <Chip
                        key={s.key}
                        label={s.label}
                        clickable
                        onClick={() => setBundleStep(idx)}
                        color={idx === bundleStep ? "primary" : "default"}
                        variant={idx === bundleStep ? "filled" : "outlined"}
                        sx={{ fontWeight: 700 }}
                    />
                ))}
            </Box>

            <Divider />

            {/* Main step content */}
            <Box sx={{ px: 2 }}>
                {active?.type === "category" ? (
                    <>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    color: "#6E7187",
                                    fontWeight: 800,
                                    textTransform: "uppercase",
                                }}
                            >
                                {active.label}
                            </Typography>
                            {Array.isArray(active?.cat?.options) &&
                            active.cat.options.length > 6 ? (
                                <TextField
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    size="small"
                                    placeholder="Search…"
                                    variant="outlined"
                                    sx={{
                                        minWidth: 220,
                                        "& .MuiOutlinedInput-root": {
                                            bgcolor: "#fff",
                                        },
                                    }}
                                />
                            ) : null}
                        </Box>

                        <Box sx={{ mt: 1 }}>
                            <Grid container spacing={1.25}>
                                {filteredCategoryOptions.map((opt) =>
                                    renderOptionCard(active.cat, opt)
                                )}
                            </Grid>
                        </Box>

                        {hasExtras ? (
                            <Box sx={{ mt: 2 }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 1,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "#6E7187",
                                            fontWeight: 800,
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        Extra add-ons
                                    </Typography>
                                    <Chip
                                        size="small"
                                        label="See all"
                                        clickable
                                        onClick={() => {
                                            const idx = steps.findIndex(
                                                (s) => s.type === "extras"
                                            );
                                            if (idx >= 0) setBundleStep(idx);
                                        }}
                                    />
                                </Box>
                                <Grid container spacing={1.25}>
                                    {allowedAddonCodes
                                        .slice(0, 6)
                                        .map((code) => renderAddonCard(code))}
                                </Grid>
                            </Box>
                        ) : null}
                    </>
                ) : null}

                {active?.type === "extras" ? (
                    <>
                        <Typography
                            variant="caption"
                            sx={{
                                color: "#6E7187",
                                fontWeight: 800,
                                textTransform: "uppercase",
                            }}
                        >
                            Extra add-ons
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                            <Grid container spacing={1.25}>
                                {allowedAddonCodes.map((code) =>
                                    renderAddonCard(code)
                                )}
                            </Grid>
                        </Box>
                    </>
                ) : null}

                {/* Notes area: always visible small; becomes large on final step */}
                <Box sx={{ mt: 2 }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                color: "#6E7187",
                                fontWeight: 800,
                                textTransform: "uppercase",
                            }}
                        >
                            Notes
                        </Typography>
                        {active?.type !== "notes" && notesIndex >= 0 ? (
                            <Chip
                                size="small"
                                label="Expand"
                                clickable
                                onClick={() => setBundleStep(notesIndex)}
                            />
                        ) : null}
                    </Box>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Add notes here"
                        className={classes?.NotesArea}
                        style={{
                            minHeight: active?.type === "notes" ? 180 : 60,
                            maxHeight: active?.type === "notes" ? 260 : 90,
                        }}
                    />
                </Box>
            </Box>

            {/* Previous / Next quick navigation */}
            <Box
                sx={{
                    px: 2,
                    pb: 2,
                    pt: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 1,
                }}
            >
                <ButtonBase
                    onClick={() => setBundleStep((s) => Math.max(0, s - 1))}
                    disabled={isFirstStep}
                    sx={{
                        flex: 1,
                        borderRadius: "10px",
                        py: 1,
                        fontWeight: 800,
                        bgcolor: isFirstStep ? "#f1f3f8" : "#6E7187",
                        color: isFirstStep ? "#9aa0b4" : "#fff",
                    }}
                >
                    Previous
                </ButtonBase>
                <ButtonBase
                    onClick={() =>
                        setBundleStep((s) => Math.min(steps.length - 1, s + 1))
                    }
                    disabled={isLastStep}
                    sx={{
                        flex: 1,
                        borderRadius: "10px",
                        py: 1,
                        fontWeight: 800,
                        bgcolor: isLastStep ? "#f1f3f8" : "#6E7187",
                        color: isLastStep ? "#9aa0b4" : "#fff",
                    }}
                >
                    Next
                </ButtonBase>
            </Box>
        </Box>
    );
}

 
